// react_frontend/src/Components/Modules/ProductSetting/ProductList.js
import React, { useState, useMemo, useContext, useEffect } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { MotherContext } from "../../../Context/MotherContext";

// RSuite
import {
  Button,
  Input,
  Badge,
  Loader,
  Whisper,
  Tooltip,
  Message,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaBox,
  FaTags,
  FaRuler,
  FaDollarSign,
  FaUser,
  FaClock,
  FaToggleOn,
  FaToggleOff,
  FaEdit,
  FaTrashAlt,
  FaPlus,
  FaSearch,
} from "react-icons/fa";
import moment from "moment";

const ProductList = () => {
  const {
    products,
    categories,
    items,
    loadingProducts,
    updateProductInList,
    removeProductFromList,
    startProductPolling,
    stopProductPolling,
    startCategoryPolling,
    stopCategoryPolling,
    startItemPolling,
    stopItemPolling,
  } = useContext(MotherContext);

  useEffect(() => {
    startProductPolling();
    startCategoryPolling();
    startItemPolling();
    return () => {
      stopProductPolling();
      stopCategoryPolling();
      stopItemPolling();
    };
  }, [
    startProductPolling,
    stopProductPolling,
    startCategoryPolling,
    stopCategoryPolling,
    startItemPolling,
    stopItemPolling,
  ]);

  const [search, setSearch] = useState("");

  //  enrichedProducts
  const enrichedProducts = useMemo(() => {
    return products
      .filter(product => product != null)
      .map(product => {
        const category = categories.find(cat => cat.id == product.category_id);
        const item = items.find(itm => itm.id == product.item_id);
        return {
          ...product,
          category_name: category?.category_name || "Unknown Category",
          item_name: item?.item_name || "Unknown Item",
        };
      }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [products, categories, items]);

  const filteredData = useMemo(() => {
    return enrichedProducts.filter((item) => {
      const cat = (item.category_name || "").toLowerCase();
      const itm = (item.item_name || "").toLowerCase();
      const code = (item.product_code || "").toLowerCase();
      const term = search.toLowerCase();
      return cat.includes(term) || itm.includes(term) || code.includes(term);
    });
  }, [enrichedProducts, search]);


  const isAdminOrAccount = ["1", "2"].includes(localStorage.getItem("who_am_i"));
  const isMasterAdmin = localStorage.getItem("who_am_i") === "1";


  // Handle Delete
  // const handleDelete = (product) => {
  //   Swal.fire({
  //     title: "Are you sure?",
  //     text: "You won't be able to recover this product!",
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonText: "Yes, delete it!",
  //     cancelButtonText: "No, cancel",
  //     reverseButtons: true,
  //   }).then(async (result) => {
  //     if (result.isConfirmed) {
  //       try {
  //         await AxiosAuthInstance.delete(`/add_product/${product.id}`);
  //         removeProductFromList(product.id);
  //         toaster.push(
  //           <Message type="success" duration={3000}>
  //             Product deleted successfully!
  //           </Message>,
  //           { placement: "topEnd" }
  //         );
  //       } catch (err) {
  //         toaster.push(
  //           <Message type="error" duration={5000}>
  //             Failed to delete product.
  //           </Message>,
  //           { placement: "topEnd" }
  //         );
  //       }
  //     }
  //   });
  // };

  // Handle Status Toggle
  const handleStatusToggle = (product) => {
    const newStatus = product.product_status === 1 ? 0 : 1;

    Swal.fire({
      title: `Are you sure?`,
      text: `You will mark "${product.item_name}" as ${newStatus ? "Active" : "Inactive"}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${newStatus ? "Activate" : "Deactivate"}`,
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.post(
            `update_product_status/${product.id}/${newStatus}`
          );
          updateProductInList({ ...product, product_status: newStatus });
          toaster.push(
            <Message type="success" duration={3000}>
              Product status updated!
            </Message>,
            { placement: "topEnd" }
          );
        } catch (err) {
          toaster.push(
            <Message type="error" duration={5000}>
              Failed to update status.
            </Message>,
            { placement: "topEnd" }
          );
        }
      }
    });
  };

  // Table Columns
  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "100px",
      center: true,
    },
    {
      name: "Category",
      selector: (row) => row.category_name,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <Badge color="blue" style={{ fontSize: "0.85em" }}>
          <FaTags /> {row.category_name || "N/A"}
        </Badge>
      ),
    },
    {
      name: "Item Name",
      selector: (row) => row.item_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaBox className="text-primary" />
          <span className="fw-medium">{row.item_name || "N/A"}</span>
        </div>
      ),
    },
    {
      name: "Code",
      selector: (row) => row.product_code,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <code className="text-dark bg-light px-2 py-1 rounded" style={{ fontSize: "0.85em" }}>
          {row.product_code}
        </code>
      ),
    },
    {
      name: "Unit",
      selector: (row) => row.product_unit,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <Badge color="cyan" style={{ fontSize: "0.8em" }}>
          <FaRuler /> {row.product_unit}
        </Badge>
      ),
    },
    {
      name: "Selling Price",
      selector: (row) => row.product_unit_price,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaDollarSign className="text-success" />
          <strong>{Number(row.product_unit_price).toLocaleString()}</strong>
        </div>
      ),
    },
    {
      name: "Status",
      width: "100px",
      center: true,
      cell: (row) => (
        <Badge
          color={row.product_status ? "green" : "red"}
          content={row.product_status ? "Active" : "Inactive"}
          style={{ fontSize: "0.8em" }}
        />
      ),
    },
    {
      name: "Actions",
      cell: (row) =>
        isAdminOrAccount ? (
          <div className="d-flex gap-1 justify-content-center">
            <Whisper placement="top" speaker={<Tooltip>{row.product_status ? "Deactivate" : "Activate"}</Tooltip>}>
              <Button
                size="sm"
                color={row.product_status ? "green" : "red"}
                startIcon={row.product_status ? <FaToggleOn /> : <FaToggleOff />}
                onClick={() => handleStatusToggle(row)}
              >
                {row.product_status ? "Active" : "Inactive"}
              </Button>
            </Whisper>

            <Whisper placement="top" speaker={<Tooltip>Edit product</Tooltip>}>
              <Link to={`/edit_product/${row.id}`} style={{ textDecoration: "none" }}>
                <Button size="sm" color="blue" startIcon={<FaEdit />}>
                  Edit
                </Button>
              </Link>
            </Whisper>

            {/* {isMasterAdmin && (
              <Whisper placement="top" speaker={<Tooltip>Delete product</Tooltip>}>
                <Button
                  size="sm"
                  color="red"
                  startIcon={<FaTrashAlt />}
                  onClick={() => handleDelete(row)}
                >
                  Delete
                </Button>
              </Whisper>
            )} */}
          </div>
        ) : (
          <Badge color="default">View Only</Badge>
        ),
      width: "200px",
      center: true,
    },
    {
      name: "Created By",
      selector: (row) => row.creator_name,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaUser className="text-secondary" />
          <small>{row.creator_name}</small>
        </div>
      ),
    },
    {
      name: "Updated By",
      selector: (row) => row.updator_name,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <small>
          {row.updator_name ? row.updator_name : <span className="text-muted">Not updated</span>}
        </small>
      ),
    },
    // {
    //   name: "Created At",
    //   selector: (row) => row.created_at,
    //   sortable: true,
    //   width: "160px",
    //   cell: (row) => (
    //     <small className="d-flex align-items-center gap-1">
    //       <FaClock className="text-secondary" />
    //       {moment(row.created_at).format("MMM DD, YYYY h:mm A")}
    //     </small>
    //   ),
    // },
    // {
    //   name: "Updated At",
    //   selector: (row) => row.updated_at,
    //   sortable: true,
    //   width: "160px",
    //   cell: (row) => (
    //     <small>
    //       {row.updated_at === row.created_at ? "—" : moment(row.updated_at).format("MMM DD, YYYY h:mm A")}
    //     </small>
    //   ),
    // },
  ];

  // Table custom styles
  const customStyles = {
    head: {
      style: { fontSize: "14px", fontWeight: "bold", backgroundColor: "#f8f9fa" },
    },
    rows: { style: { fontSize: "14px", minHeight: "60px" } },
    cells: { style: { padding: "12px 8px" } },
  };

  const AddProductButton = () =>
    isAdminOrAccount ? (
      <Whisper placement="top" speaker={<Tooltip>Add new product</Tooltip>}>
        <Link to="/add_product" style={{ textDecoration: "none" }}>
          <Button appearance="primary" startIcon={<FaPlus />} className="d-flex align-items-center custom-bg">
            Add Product
          </Button>
        </Link>
      </Whisper>
    ) : null;

  return (
    <>
      <Breadcrumb title="Product Management" />

      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <FaBox className="me-2" />
          Products
        </h5>

        <div className="d-flex gap-2 w-100 w-md-auto">
          <Input
            placeholder="Search by category, item, or code..."
            value={search}
            onChange={setSearch}
            prefix={<FaSearch />}
            style={{ width: 300 }}
            size="md"
          />
          <AddProductButton />
        </div>
      </div>

      <DataTable
        keyField="id"
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 250, 500, 1000]}
        progressPending={loadingProducts}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading products...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <FaBox size={48} className="mb-3 text-secondary" />
            <h5>No products found</h5>
            <p>Try adjusting your search or add a new product.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />
    </>
  );
};

export default ProductList;
